import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  X,
  CheckCircle2,
  Clock,
  Play,
  Square,
  Loader2,
  User,
  Globe,
  ShieldCheck,
  Users,
  Filter,
  Trash2,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { iotControlData as tvControlData, translations as tvTranslations } from "../data/NewControlData";
import { ServerUrl } from "../config";

const LANGUAGES = [
  { key: "spanish", label: "Spanish (Español)" },
  { key: "english", label: "English" },
  { key: "french", label: "French (Français)" },
  { key: "german", label: "German (Deutsch)" },
  { key: "italian", label: "Italian (Italiano)" },
  { key: "dutch", label: "Dutch (Nederlands)" },
];

const norm = (s) => (s || "").toLowerCase().trim();

// Guess a contributor's working language from their email (e.g. lb_de_01 -> german).
const LANG_BY_PREFIX = { es: "spanish", en: "english", fr: "french", de: "german", it: "italian", nl: "dutch" };

// Pull the utterance list for a given voice-control + language, with sensible fallback.
const utterancesFor = (catKey, vcKey, langKey) => {
  const langMap = tvControlData?.[catKey]?.[vcKey];
  if (!langMap) return [];
  return langMap[langKey] || langMap["spanish"] || langMap["english"] || [];
};

const COLORS = {
  blue: "#3b82f6",
  emerald: "#10b981",
  amber: "#f59e0b",
  rose: "#f43f5e",
  slate: "#1e293b",
  track: "#e2e8f0",
};

function ProgressBar({ percent, tone = "blue" }) {
  const auto =
    percent >= 100 ? COLORS.emerald : percent >= 50 ? COLORS.blue : percent > 0 ? COLORS.amber : COLORS.rose;
  const fill = tone === "auto" ? auto : COLORS[tone] || COLORS.blue;
  return (
    <div style={{ height: 8, width: "100%", borderRadius: 9999, background: COLORS.track, overflow: "hidden" }}>
      <div
        style={{
          height: "100%",
          borderRadius: 9999,
          background: fill,
          width: `${Math.min(100, Math.max(0, percent))}%`,
          transition: "width .5s ease",
        }}
      />
    </div>
  );
}

function StatPill({ label, value, tone }) {
  return (
    <div className="text-center px-2">
      <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#94a3b8" }}>
        {label}
      </p>
      <p className="text-lg font-extrabold leading-tight" style={{ color: COLORS[tone] || COLORS.slate }}>
        {value}
      </p>
    </div>
  );
}

function Badge({ bg, fg, children }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full font-bold shrink-0 whitespace-nowrap"
      style={{ background: bg, color: fg, fontSize: 10, padding: "2px 8px" }}
    >
      {children}
    </span>
  );
}

function UtteranceTrackerModal({ isOpen, onClose, targetUserEmail = "", initialLanguage = "" }) {
  const userRole = localStorage.getItem("role") || "user";
  const isSuper = userRole === "superadmin";

  // When opened from a user's "View Progress" row, lock the report to that one contributor.
  const lockedUser = (targetUserEmail || "").trim();

  const [audioDetails, setAudioDetails] = useState([]);
  const [userList, setUserList] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  // Filters
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [selectedUser, setSelectedUser] = useState(targetUserEmail || "");
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage || "");

  // Drilldown
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedVc, setExpandedVc] = useState(null);
  // Locked (single-user) view: show only what the user actually submitted.
  const [submittedOnly, setSubmittedOnly] = useState(true);

  // Audio playback
  const [loadingAudioId, setLoadingAudioId] = useState(null);
  const [activeAudio, setActiveAudio] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);

  const categories = useMemo(() => Object.keys(tvControlData), []);
  const autoLangDone = useRef(false);

  useEffect(() => {
    if (isOpen) {
      fetchAudioDetails();
      fetchUsersList();
      setSelectedUser(targetUserEmail || "");
      setSelectedLanguage(initialLanguage || "");
      setSelectedAdmin("");
      setExpandedCategory(null);
      setExpandedVc(null);
      setSubmittedOnly(true);
      autoLangDone.current = false;
    }
  }, [isOpen, targetUserEmail, initialLanguage]);

  // For a locked contributor, auto-pick the language they actually work in.
  const guessedLang = useMemo(() => {
    if (!lockedUser) return "";
    const counts = {};
    audioDetails.forEach((a) => {
      if (norm(a.uploaderEmail || a.email) !== norm(lockedUser)) return;
      const l = norm(a.audioLanguage);
      if (l) counts[l] = (counts[l] || 0) + 1;
    });
    const top = Object.entries(counts).sort((x, y) => y[1] - x[1])[0];
    if (top && top[1] > 0) return top[0];
    const m = /lb_([a-z]{2})_/i.exec(lockedUser);
    return (m && LANG_BY_PREFIX[m[1].toLowerCase()]) || "";
  }, [lockedUser, audioDetails]);

  useEffect(() => {
    if (isOpen && lockedUser && !initialLanguage && !autoLangDone.current && guessedLang) {
      autoLangDone.current = true;
      setSelectedLanguage(guessedLang);
    }
  }, [isOpen, lockedUser, initialLanguage, guessedLang]);

  useEffect(() => {
    if (!isOpen) {
      setAudioDetails([]);
      setSelectedAdmin("");
      setSelectedUser("");
      setSelectedLanguage("");
      setExpandedCategory(null);
      setExpandedVc(null);
      if (activeAudio) {
        activeAudio.pause();
        setActiveAudio(null);
      }
      setPlayingAudioId(null);
    }
  }, [isOpen]);

  // Reset the deeper drilldown whenever a higher-level filter changes.
  useEffect(() => {
    setExpandedCategory(null);
    setExpandedVc(null);
  }, [selectedLanguage, selectedUser, selectedAdmin]);

  // Lock the background page scroll while the modal is open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const fetchAudioDetails = async () => {
    setIsFetching(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}audios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setAudioDetails(response.data.data || []);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setAudioDetails([]);
      } else {
        console.error("Failed to fetch audio details:", error);
      }
    } finally {
      setIsFetching(false);
    }
  };

  const fetchUsersList = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setUserList(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch user list:", error);
    }
  };

  const getTranslatedLabel = (key, type) => {
    if (!key) return "";
    const map = type === "category" ? tvTranslations.categories : tvTranslations.voiceControls;
    const lang = selectedLanguage || "spanish";
    return map[key]?.[lang] || map[key]?.["spanish"] || map[key]?.["english"] || key;
  };

  const adminUsersList = useMemo(() => userList.filter((u) => u.role === "admin"), [userList]);

  const availableUsersList = useMemo(() => {
    if (isSuper && selectedAdmin) {
      return userList.filter((u) => u.createdBy === selectedAdmin);
    }
    return userList.filter((u) => u.role !== "superadmin");
  }, [userList, isSuper, selectedAdmin]);

  // Total expected Sample Utterances for the selected language (all categories / voice controls).
  const expectedTotal = useMemo(() => {
    if (!selectedLanguage) return 0;
    let n = 0;
    categories.forEach((cat) => {
      Object.keys(tvControlData[cat] || {}).forEach((vc) => {
        n += utterancesFor(cat, vc, selectedLanguage).length;
      });
    });
    return n;
  }, [selectedLanguage, categories]);

  const expectedSet = useMemo(() => {
    const set = new Set();
    if (!selectedLanguage) return set;
    categories.forEach((cat) => {
      Object.keys(tvControlData[cat] || {}).forEach((vc) => {
        utterancesFor(cat, vc, selectedLanguage).forEach((u) => set.add(`${vc}||${u}`));
      });
    });
    return set;
  }, [selectedLanguage, categories]);

  // Per-contributor totals: raw sample count + collected count for the selected language.
  const contributorProgress = useMemo(() => {
    const acc = {};
    audioDetails.forEach((a) => {
      const email = norm(a.uploaderEmail || a.email);
      if (!email) return;
      if (!acc[email]) acc[email] = { samples: 0, keys: new Set() };
      acc[email].samples += 1;
      if (selectedLanguage && norm(a.audioLanguage) === norm(selectedLanguage)) {
        [a.sampleUtterance, a.userInput].forEach((t) => {
          if (t) acc[email].keys.add(`${a.audiotext}||${t}`);
        });
      }
    });
    const out = {};
    Object.entries(acc).forEach(([email, v]) => {
      let collected = 0;
      v.keys.forEach((k) => {
        if (expectedSet.has(k)) collected += 1;
      });
      out[email] = { samples: v.samples, collected: selectedLanguage ? collected : 0 };
    });
    return out;
  }, [audioDetails, selectedLanguage, expectedSet]);

  const progressLabel = (email) => {
    const p = contributorProgress[norm(email)] || { samples: 0, collected: 0 };
    if (selectedLanguage) return `${p.collected}/${expectedTotal} collected`;
    return `${p.samples} sample${p.samples === 1 ? "" : "s"}`;
  };

  const combinedProgressLabel = () => {
    const emails = new Set(availableUsersList.map((u) => norm(u.email)));
    let samples = 0;
    Object.entries(contributorProgress).forEach(([email, p]) => {
      if (emails.size === 0 || emails.has(email)) samples += p.samples;
    });
    return `${samples} sample${samples === 1 ? "" : "s"} total`;
  };

  // Audios narrowed to the current contributor filter (admin team + specific user).
  const filteredAudios = useMemo(() => {
    return audioDetails.filter((item) => {
      const uploader = norm(item.uploaderEmail || item.email);

      if (isSuper && selectedAdmin) {
        const teamEmails = userList
          .filter((u) => u.createdBy === selectedAdmin)
          .map((u) => norm(u.email));
        const inTeam = teamEmails.includes(uploader) || uploader === norm(selectedAdmin);
        if (!inTeam) return false;
      }

      if (selectedUser && uploader !== norm(selectedUser)) return false;

      return true;
    });
  }, [audioDetails, isSuper, selectedAdmin, selectedUser, userList]);

  // Fast lookup of what has been collected: key = "lang||voiceControl||utterance".
  const { collectedSet, audioByKey } = useMemo(() => {
    const set = new Set();
    const byKey = {};
    filteredAudios.forEach((a) => {
      const lang = norm(a.audioLanguage);
      const vc = a.audiotext;
      [a.sampleUtterance, a.userInput].forEach((txt) => {
        if (!txt) return;
        const key = `${lang}||${vc}||${txt}`;
        set.add(key);
        if (!byKey[key]) byKey[key] = a;
      });
    });
    return { collectedSet: set, audioByKey: byKey };
  }, [filteredAudios]);

  const isCollected = (vcKey, uText) =>
    collectedSet.has(`${norm(selectedLanguage)}||${vcKey}||${uText}`);

  const getAudioData = (vcKey, uText) =>
    audioByKey[`${norm(selectedLanguage)}||${vcKey}||${uText}`] || null;

  // Per-voice-control progress for the selected language.
  const vcStats = (catKey, vcKey) => {
    const list = utterancesFor(catKey, vcKey, selectedLanguage);
    const total = list.length;
    let collected = 0;
    list.forEach((u) => {
      if (isCollected(vcKey, u)) collected++;
    });
    return { total, collected, pending: Math.max(0, total - collected) };
  };

  // Per-category progress = sum of its voice controls.
  const categoryStats = (catKey) => {
    let total = 0;
    let collected = 0;
    Object.keys(tvControlData[catKey] || {}).forEach((vc) => {
      const s = vcStats(catKey, vc);
      total += s.total;
      collected += s.collected;
    });
    const pending = Math.max(0, total - collected);
    const percent = total > 0 ? Math.round((collected / total) * 100) : 0;
    return { total, collected, pending, percent };
  };

  // Overall progress across every category for the selected contributor + language.
  const overall = useMemo(() => {
    if (!selectedLanguage) return { total: 0, collected: 0, pending: 0, percent: 0 };
    let total = 0;
    let collected = 0;
    categories.forEach((cat) => {
      const s = categoryStats(cat);
      total += s.total;
      collected += s.collected;
    });
    const pending = Math.max(0, total - collected);
    const percent = total > 0 ? Math.round((collected / total) * 100) : 0;
    return { total, collected, pending, percent };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage, filteredAudios, categories]);

  const contributorLabel = selectedUser
    ? selectedUser
    : isSuper && selectedAdmin
    ? `${selectedAdmin}'s team`
    : "All my users";

  // ---- Audio playback ----
  const handlePlayAudio = async (fileName, audioId) => {
    if (activeAudio) {
      activeAudio.pause();
      if (playingAudioId === audioId) {
        setActiveAudio(null);
        setPlayingAudioId(null);
        return;
      }
    }
    setLoadingAudioId(audioId);
    try {
      const response = await axios.post(
        `${ServerUrl}stream_audio`,
        { file: fileName },
        { responseType: "blob" }
      );
      const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.onended = () => {
        setPlayingAudioId(null);
        setActiveAudio(null);
      };
      setActiveAudio(audio);
      setPlayingAudioId(audioId);
      audio.play();
    } catch (error) {
      Swal.fire("Error", "Failed to load audio: " + error.message, "error");
    } finally {
      setLoadingAudioId(null);
    }
  };

  const handleDeleteAudio = async (audioId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.delete(`${ServerUrl}delete_audio/${audioId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.status === 200) {
          Swal.fire("Deleted!", "The audio sample has been deleted.", "success");
          fetchAudioDetails();
        }
      } catch (error) {
        console.error("Failed to delete audio:", error);
        Swal.fire("Error", "Failed to delete audio: " + (error.response?.data?.message || error.message), "error");
      }
    }
  };

  if (!isOpen) return null;

  const ready = !!selectedLanguage;

  return (
    <div
      className="fixed inset-0 flex items-stretch sm:items-center justify-center z-50 p-0 sm:p-4"
      style={{ background: "rgba(15,23,42,0.6)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full h-full sm:h-[86vh] sm:max-w-3xl rounded-none sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
        style={{ background: "#ffffff" }}
      >
        {/* Header */}
        <div
          className="flex justify-between items-center gap-3 px-4 sm:px-6 py-3 shrink-0"
          style={{ background: "#0f172a", color: "#ffffff" }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {isSuper ? (
              <ShieldCheck size={20} style={{ color: "#facc15" }} className="shrink-0" />
            ) : (
              <Users size={20} style={{ color: "#60a5fa" }} className="shrink-0" />
            )}
            <div className="min-w-0">
              <h2 className="text-sm sm:text-base font-bold truncate">
                {lockedUser ? "User Progress Report" : "Contributor Progress Report"}
              </h2>
              <p className="text-[11px] hidden sm:block truncate" style={{ color: "#cbd5e1" }}>
                {lockedUser
                  ? lockedUser
                  : "See how many Sample Utterances each user has collected — and what's left."}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold shrink-0 transition-colors"
            style={{ background: "#e11d48", color: "#ffffff" }}
          >
            <X size={16} />
            <span className="hidden sm:inline">Close</span>
          </button>
        </div>

        {/* Filters */}
        <div className="px-4 sm:px-6 py-3 bg-slate-50 border-b border-slate-200 shrink-0">
          <div className={`grid grid-cols-1 gap-2 sm:gap-3 ${lockedUser ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
            {!lockedUser && isSuper && (
              <div>
                <label className="flex items-center text-[11px] font-bold text-slate-600 mb-1 uppercase tracking-wider">
                  <ShieldCheck size={13} className="mr-1.5 text-yellow-600" />
                  Admin
                </label>
                <select
                  value={selectedAdmin}
                  onChange={(e) => {
                    setSelectedAdmin(e.target.value);
                    setSelectedUser("");
                  }}
                  className="w-full px-3 py-2 text-sm font-medium text-slate-900 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-yellow-500 focus:outline-none cursor-pointer"
                >
                  <option value="">All admin accounts</option>
                  {adminUsersList.map((a) => {
                    const count = userList.filter((u) => u.createdBy === a.email).length;
                    return (
                      <option key={a._id} value={a.email}>
                        {a.email} ({count} users)
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            <div>
              <label className="flex items-center text-[11px] font-bold text-slate-600 mb-1 uppercase tracking-wider">
                <User size={13} className="mr-1.5 text-blue-600" />
                Contributor
              </label>
              {lockedUser ? (
                <div className="w-full px-3 py-2 text-sm font-semibold text-slate-900 border border-slate-200 rounded-lg bg-slate-100 truncate">
                  {lockedUser}
                </div>
              ) : (
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-3 py-2 text-sm font-medium text-slate-900 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                >
                  <option value="">
                    {(isSuper && selectedAdmin ? `All users of ${selectedAdmin}` : "All my users (combined)") +
                      ` — ${combinedProgressLabel()}`}
                  </option>
                  {availableUsersList.map((u) => (
                    <option key={u._id} value={u.email}>
                      {u.email} ({u.role}) — {progressLabel(u.email)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="flex items-center text-[11px] font-bold text-slate-600 mb-1 uppercase tracking-wider">
                <Globe size={13} className="mr-1.5 text-blue-600" />
                Language
              </label>
              {lockedUser && selectedLanguage ? (
                <div className="w-full px-3 py-2 text-sm font-semibold text-slate-900 border border-slate-200 rounded-lg bg-slate-100 capitalize truncate">
                  {LANGUAGES.find((l) => l.key === selectedLanguage)?.label || selectedLanguage}
                </div>
              ) : (
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="w-full px-3 py-2 text-sm font-medium text-slate-900 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                >
                  <option value="">Select language…</option>
                  {LANGUAGES.map((l) => (
                    <option key={l.key} value={l.key}>
                      {l.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        {!ready ? (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center" style={{ background: "#ffffff" }}>
            <div className="p-4 rounded-full mb-4 border" style={{ background: "#eff6ff", color: "#2563eb", borderColor: "#dbeafe" }}>
              <Filter size={34} />
            </div>
            <h3 className="text-base font-bold text-slate-800 mb-1">Pick a language to see the report</h3>
            <p className="text-xs text-slate-500 max-w-sm leading-relaxed">
              {lockedUser ? (
                <>Select a <strong>Language</strong> to see this user's collected vs. pending Sample Utterances.</>
              ) : (
                <>Choose a <strong>Contributor</strong> and a <strong>Language</strong> for a clear
                category-by-category breakdown of collected vs. pending Sample Utterances.</>
              )}
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ background: "#ffffff" }}>
            {/* Hero summary */}
            <div className="px-4 sm:px-6 py-4 border-b border-slate-200" style={{ background: "#f8fafc" }}>
              <div className="flex flex-col gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#1d4ed8" }}>
                    Overall progress
                  </p>
                  <h3 className="text-base sm:text-lg font-extrabold text-slate-900 truncate">{contributorLabel}</h3>
                  <p className="text-xs text-slate-500 capitalize">Language: {selectedLanguage}</p>
                </div>
                <div
                  className="grid grid-cols-3 divide-x divide-slate-200 rounded-lg border border-slate-200 py-2"
                  style={{ background: "#ffffff" }}
                >
                  <StatPill label="Collected" value={overall.collected} tone="emerald" />
                  <StatPill label="Pending" value={overall.pending} tone="rose" />
                  <StatPill label="Total" value={overall.total} tone="slate" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <ProgressBar percent={overall.percent} tone="auto" />
                </div>
                <span className="text-sm font-extrabold text-slate-800 tabular-nums w-10 text-right shrink-0">
                  {overall.percent}%
                </span>
              </div>
            </div>

            {/* Category list */}
            {isFetching ? (
              <div className="flex justify-center items-center h-40 text-slate-500 text-sm">
                <Loader2 size={18} className="animate-spin mr-2 text-blue-600" />
                Loading progress…
              </div>
            ) : (
              <div className="p-3 sm:p-5 space-y-2.5">
                {lockedUser && (
                  <label className="flex items-center gap-2 px-1 text-xs font-semibold text-slate-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={submittedOnly}
                      onChange={(e) => setSubmittedOnly(e.target.checked)}
                      className="accent-blue-600"
                    />
                    Show only what this user submitted
                  </label>
                )}

                {(() => {
                  const visibleCats = categories.filter(
                    (cat) => !(lockedUser && submittedOnly) || categoryStats(cat).collected > 0
                  );
                  if (visibleCats.length === 0) {
                    return (
                      <div className="py-10 text-center text-sm text-slate-500">
                        This user hasn't submitted any Sample Utterances
                        {selectedLanguage ? ` in ${selectedLanguage}` : ""} yet.
                      </div>
                    );
                  }
                  return (
                    <>
                      <div className="flex items-center justify-between px-1 gap-2">
                        <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          {lockedUser && submittedOnly ? "Submitted categories" : "By category"} ({visibleCats.length})
                        </h4>
                        <span className="text-[11px] text-slate-400 hidden sm:inline">Tap a row to drill down</span>
                      </div>

                      {visibleCats.map((cat) => {
                        const s = categoryStats(cat);
                        const open = expandedCategory === cat;
                        return (
                    <div
                      key={cat}
                      className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs"
                    >
                      {/* Category row */}
                      <button
                        onClick={() => {
                          setExpandedCategory(open ? null : cat);
                          setExpandedVc(null);
                        }}
                        className="w-full flex items-start gap-2.5 px-3 sm:px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                      >
                        {open ? (
                          <ChevronDown size={16} className="text-slate-400 shrink-0 mt-0.5" />
                        ) : (
                          <ChevronRight size={16} className="text-slate-400 shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm font-bold text-slate-800 break-words min-w-0">
                              {getTranslatedLabel(cat, "category")}
                            </p>
                            {s.total === 0 ? (
                              <Badge bg="#f1f5f9" fg="#64748b">No data</Badge>
                            ) : s.pending > 0 ? (
                              <Badge bg="#ffe4e6" fg="#be123c">{s.pending} left</Badge>
                            ) : (
                              <Badge bg="#d1fae5" fg="#047857">
                                <CheckCircle2 size={11} /> Done
                              </Badge>
                            )}
                          </div>
                          <div className="mt-2 flex items-center gap-2.5">
                            <div className="flex-1 min-w-0">
                              <ProgressBar percent={s.percent} tone="auto" />
                            </div>
                            <span
                              className="text-xs font-bold tabular-nums shrink-0"
                              style={{ color: "#475569" }}
                            >
                              {s.collected}/{s.total}
                              <span style={{ color: "#94a3b8", fontWeight: 600 }}> · {s.percent}%</span>
                            </span>
                          </div>
                        </div>
                      </button>

                      {/* Voice controls */}
                      {open && (
                        <div className="border-t border-slate-200 px-3 py-2 space-y-1" style={{ background: "#f8fafc" }}>
                          {Object.keys(tvControlData[cat] || {})
                            .filter((vc) => !(lockedUser && submittedOnly) || vcStats(cat, vc).collected > 0)
                            .map((vc) => {
                            const vs = vcStats(cat, vc);
                            const vcOpen = expandedVc === `${cat}::${vc}`;
                            return (
                              <div key={vc} className="rounded-lg overflow-hidden">
                                <button
                                  onClick={() => setExpandedVc(vcOpen ? null : `${cat}::${vc}`)}
                                  className="w-full flex items-center gap-2 px-2.5 sm:px-3 py-2 hover:bg-white rounded-lg transition-colors text-left"
                                >
                                  {vcOpen ? (
                                    <ChevronDown size={14} className="text-slate-400 shrink-0" />
                                  ) : (
                                    <ChevronRight size={14} className="text-slate-400 shrink-0" />
                                  )}
                                  <span className="flex-1 text-xs font-semibold text-slate-700 truncate min-w-0">
                                    {getTranslatedLabel(vc, "voiceControl")}
                                  </span>
                                  <span className="text-[11px] text-slate-500 tabular-nums shrink-0">
                                    {vs.collected}/{vs.total}
                                  </span>
                                  <div className="w-12 sm:w-20 shrink-0">
                                    <ProgressBar
                                      percent={vs.total > 0 ? Math.round((vs.collected / vs.total) * 100) : 0}
                                      tone="auto"
                                    />
                                  </div>
                                </button>

                                {/* Utterance list */}
                                {vcOpen && (
                                  <div className="bg-white border border-slate-200 rounded-lg mx-1 my-1 overflow-x-auto">
                                    <table className="w-full min-w-[320px] text-left text-xs">
                                      <thead>
                                        <tr className="bg-slate-100 text-slate-600 font-bold">
                                          <th className="py-2 px-2 w-8 text-center">#</th>
                                          <th className="py-2 px-2">Sample Utterance</th>
                                          <th className="py-2 px-2 w-24 text-center">Status</th>
                                          <th className="py-2 px-2 w-16 text-center">Audio</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {utterancesFor(cat, vc, selectedLanguage)
                                          .map((uText, idx) => ({ uText, idx, done: isCollected(vc, uText) }))
                                          .filter((r) => !(lockedUser && submittedOnly) || r.done)
                                          .map(({ uText, idx, done }) => {
                                          const audioData = getAudioData(vc, uText);
                                          return (
                                            <tr key={idx} className={done ? "bg-emerald-50/30 align-top" : "align-top"}>
                                              <td className="py-2 px-2 text-center text-slate-400 font-mono">
                                                {idx + 1}
                                              </td>
                                              <td className="py-2 px-2 font-medium text-slate-800 break-words">{uText}</td>
                                              <td className="py-2 px-2 text-center">
                                                {done ? (
                                                  <Badge bg="#d1fae5" fg="#047857">
                                                    <CheckCircle2 size={11} /> Collected
                                                  </Badge>
                                                ) : (
                                                  <Badge bg="#ffe4e6" fg="#be123c">
                                                    <Clock size={11} /> Pending
                                                  </Badge>
                                                )}
                                              </td>
                                              <td className="py-2 px-2 text-center">
                                                {done && audioData ? (
                                                  <div className="flex items-center justify-center gap-1.5">
                                                    {loadingAudioId === audioData._id ? (
                                                      <Loader2 size={13} className="animate-spin text-blue-600" />
                                                    ) : (
                                                      <button
                                                        onClick={() =>
                                                          handlePlayAudio(audioData.fileName, audioData._id)
                                                        }
                                                        className="p-1.5 rounded-full transition-colors"
                                                        style={{
                                                          background:
                                                            playingAudioId === audioData._id ? "#e11d48" : "#059669",
                                                          color: "#ffffff",
                                                        }}
                                                      >
                                                        {playingAudioId === audioData._id ? (
                                                          <Square size={9} />
                                                        ) : (
                                                          <Play size={9} />
                                                        )}
                                                      </button>
                                                    )}
                                                    <button
                                                      onClick={() => handleDeleteAudio(audioData._id)}
                                                      className="p-1.5 rounded-full transition-colors"
                                                      style={{ color: "#94a3b8" }}
                                                      title="Delete audio"
                                                    >
                                                      <Trash2 size={11} />
                                                    </button>
                                                  </div>
                                                ) : (
                                                  <span style={{ color: "#cbd5e1" }}>—</span>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div
          className="px-4 sm:px-6 py-2.5 border-t border-slate-200 flex justify-between items-center gap-3 text-xs shrink-0"
          style={{ background: "#f8fafc", color: "#64748b" }}
        >
          <span className="truncate min-w-0">
            {ready ? (
              <>
                <strong className="capitalize" style={{ color: "#1e293b" }}>
                  {selectedLanguage}
                </strong>{" "}
                — {overall.collected}/{overall.total} collected
              </>
            ) : (
              <span>Select filters to start</span>
            )}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 font-semibold rounded-md transition-colors shrink-0"
            style={{ background: "#1e293b", color: "#ffffff" }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default UtteranceTrackerModal;
